<script lang="ts" generics="TCommitReceipt = undefined">
  import type { Snippet } from "svelte"
  import { onDestroy } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import StepActionCTA from "../../../../../step-action-cta"
  import {
    WORK_ORDER_ACTION_TOOLTIP_CLASS,
    workOrderActionTooltips,
  } from "../../../../../../_logic/components-work-orders-action-tooltips"
  import type {
    ContactOrderPlanningOperations,
    ContactOrderPlanningSubmissionPhase,
    ContactOrderPlanningSubmissionResult,
  } from "../../../../../../_logic/contact-orders-contract"
  import type { ContactOrderCreateIntent } from "../../../../create-contact-orders-contract"
  import { projectContactOrderSubmissionResult } from "./project-submission-result"

  const LONG_RUNNING_BATCH_NOTIFICATION_THRESHOLD = 50

  type Props = {
    children?: Snippet<[Snippet]>
    intent: ContactOrderCreateIntent
    operations: ContactOrderPlanningOperations<TCommitReceipt>
    phase: ContactOrderPlanningSubmissionPhase
    onPhaseChange: (phase: ContactOrderPlanningSubmissionPhase) => void
    onSubmissionResult: (
      result: ContactOrderPlanningSubmissionResult<TCommitReceipt>,
    ) => void | Promise<void>
  }

  const {
    children = null,
    intent,
    operations,
    phase,
    onPhaseChange,
    onSubmissionResult,
  }: Props = $props()

  let pending = $state(false)
  let active = true
  onDestroy(() => {
    active = false
  })
  const disabled = $derived(pending || phase !== "idle" || intent.kind === "blocked")
  const disabledReason = $derived(
    pending || phase === "creating_contact_orders"
      ? "Ankündigungspläne werden gerade angelegt."
      : phase === "continuing"
        ? "Der Workflow wird gerade fortgesetzt."
        : intent.disabledReason,
  )
  const tooltipContent = $derived(
    intent.kind === "blocked" && intent.pendingCount > 0
      ? workOrderActionTooltips.batchNoContactOrdersAvailable
      : workOrderActionTooltips.batchCreateContactOrders,
  )

  const notifyResult = (
    committedIntent: Extract<ContactOrderCreateIntent, { kind: "commit" }>,
    result: ContactOrderPlanningSubmissionResult<TCommitReceipt>,
  ) => {
    if (result.kind !== "committed") return

    if (result.completedCount) {
      newNotification({
        class: "success",
        html: committedIntent.successMessage(result.completedCount),
      })
    }

    const failedCount = result.result.result.failed?.length ?? 0

    if (failedCount) {
      newNotification({
        class: "error",
        html: `${failedCount} Ankündigungspläne konnten nicht angelegt werden.`,
      })
    }
  }

  const emitRemoved = async (
    currentIntent: Extract<ContactOrderCreateIntent, { kind: "remove_only" }>,
  ) => {
    try {
      await onSubmissionResult({
        kind: "removed",
        removedEventIds: [...currentIntent.removedEventIds],
      })
    } catch (error) {
      console.error("contact_order_planning_row_removal_transition_failed", error)
      newNotification({
        class: "error",
        html: "Entfernte Zeilen konnten nicht in den Workflow übernommen werden.",
      })
    }
  }

  const run = async () => {
    if (pending || phase !== "idle") return

    if (intent.kind === "blocked") {
      const message = intent.message ?? intent.disabledReason

      if (message) newNotification({ class: "error", html: message })

      return
    }

    if (intent.kind === "remove_only") {
      pending = true
      onPhaseChange("continuing")
      try {
        await emitRemoved(intent)
      } finally {
        pending = false
        onPhaseChange("idle")
      }

      return
    }

    pending = true
    onPhaseChange("creating_contact_orders")
    const committedIntent = intent
    const completeSubmission = onSubmissionResult
    const createContactOrders = operations.createContactOrders
    try {
      const automaticCount = committedIntent.command.payloads.filter(
        (payload) =>
          payload.communication_selection?.postal.mode === "PERSONAL" &&
          payload.communication_selection.postal.dispatch_mode === "AUTOMATIC",
      ).length

      if (
        automaticCount &&
        !(await newApproval({
          title: `${automaticCount} Terminbriefe automatisch vorsehen?`,
          description:
            "Bei Fälligkeit werden diese Briefe ohne weitere Einzelprüfung kostenpflichtig an die bestätigten Empfänger beauftragt, sofern sie noch erforderlich sind. Es gibt keine zusätzliche Tages- oder Kostenobergrenze. Alle übrigen Ankündigungen werden ebenfalls gespeichert.",
          approvalLabel: "Ankündigungspläne anlegen",
          declineLabel: "Zurück zur Auswahl",
        }))
      )
        return

      if (!active) return

      if (committedIntent.command.targetEvents.length > LONG_RUNNING_BATCH_NOTIFICATION_THRESHOLD) {
        newNotification({
          class: "info",
          html: "Es werden gerade mehr als 50 Aufträge für die Kundeninformation verarbeitet. Das kann einen Moment dauern.",
        })
      }

      const operationResult = await createContactOrders(committedIntent.command)

      const result = projectContactOrderSubmissionResult(committedIntent, operationResult)

      if (active) notifyResult(committedIntent, result)

      try {
        // A committed operation still belongs to its original owner after the
        // overlay closes. That owner refreshes its list without reopening it.
        await completeSubmission(result)
      } catch (error) {
        console.error("contact_order_planning_surface_completion_failed", error)
        newNotification({
          class: "error",
          html: "Änderung gespeichert, Oberfläche konnte nicht aktualisiert werden.",
        })
      }
    } catch (error) {
      if (!active) return

      console.error("contact_order_planning_surface_create_failed", error)
      newNotification({
        class: "error",
        html:
          error instanceof Error
            ? error.message
            : "Ankündigungspläne konnten nicht angelegt werden.",
      })
    } finally {
      pending = false

      if (active) onPhaseChange("idle")
    }
  }
</script>

{#snippet primaryAction()}
  <StepActionCTA
    type="primaryButton"
    content={pending ? "Verarbeite…" : intent.label}
    {disabled}
    {disabledReason}
    {tooltipContent}
    tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
    onClick={run}
  />
{/snippet}

{#if children}
  {@render children(primaryAction)}
{:else}
  {@render primaryAction()}
{/if}
